/**
 * EmployeeDetailDrawer — employee detail with payroll, modelled on
 * retail/ProductDetailDrawer. Opened by a row click in StaffList. Sections:
 *   • header        — name + role + active StatusPill
 *   • Личност       — DOB, ЕМБГ, address, emergency contact
 *   • Вработување   — role, employment type, hired on, locale
 *   • Плата         — бруто / нето via <Money>, IBAN (mono)
 *   • Белешки       — free-text notes
 * Footer (single row): Запиши плата (record gross salary as a monthly expense),
 * Измени (edit → StaffForm in a modal), Деактивирај/Активирај (toggle is_active).
 * Owner/manager only (route-guarded; `hasRole` re-checked here).
 */
import { Pencil, Power, Wallet } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { SideDrawer } from '../../components/ui/Drawer.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { Chip, StatusPill } from '../../components/ui/StatusPill.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';

import {
  NoSalarySetError,
  type StaffRow,
  useRecordSalaryExpense,
  useUpdateStaff,
} from './hooks/useStaff.js';
import { StaffForm } from './StaffForm.js';

/** Two-column label/value row inside a section card. */
function DetailRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="shrink-0 text-sm text-muted">{label}</span>
      <span className="text-right text-sm font-medium text-fg">{children}</span>
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }): ReactNode {
  return (
    <h4 className="mb-2.5 font-display text-sm font-semibold uppercase tracking-wide text-muted">
      {children}
    </h4>
  );
}

export function EmployeeDetailDrawer({
  open,
  onOpenChange,
  staff,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staff: StaffRow | null;
}): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const { hasRole } = useAuth();
  const canManage = hasRole('owner', 'manager');
  const recordSalary = useRecordSalaryExpense();
  const toggleActive = useUpdateStaff();
  const [editOpen, setEditOpen] = useState(false);

  if (!staff) return null;

  const busy = recordSalary.isPending || toggleActive.isPending;

  function onRecordSalary(s: StaffRow): void {
    recordSalary.mutate(s, {
      onSuccess: () => toast.success(t('staff.salaryRecorded')),
      onError: (e) => {
        if (e instanceof NoSalarySetError) toast.error(t('staff.noSalarySet'));
        else toast.error(t('common.error'), e instanceof Error ? e.message : undefined);
      },
    });
  }

  function onToggleActive(s: StaffRow): void {
    toggleActive.mutate(
      { id: s.id, input: { isActive: !s.is_active } as never },
      {
        onSuccess: () => {
          toast.success(t('common.saved'));
          onOpenChange(false);
        },
        onError: (e) => toast.error(t('common.error'), e instanceof Error ? e.message : undefined),
      },
    );
  }

  const employmentLabel = staff.employment_type
    ? t('staff.employmentType.' + staff.employment_type, { defaultValue: staff.employment_type })
    : null;

  return (
    <>
      <SideDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={`${staff.first_name} ${staff.last_name}`}
        subtitle={t('staff.roles.' + staff.app_role)}
        width="xl"
        footer={
          canManage ? (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                loading={recordSalary.isPending}
                disabled={toggleActive.isPending}
                onClick={() => onRecordSalary(staff)}
              >
                <Wallet className="h-4 w-4" aria-hidden />
                {t('staff.recordSalary')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="h-4 w-4" aria-hidden />
                {t('common.edit')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={toggleActive.isPending}
                disabled={recordSalary.isPending}
                onClick={() => onToggleActive(staff)}
              >
                <Power className="h-4 w-4" aria-hidden />
                {staff.is_active ? t('common.deactivate') : t('common.activate')}
              </Button>
            </div>
          ) : undefined
        }
      >
        {/* ── Header card ──────────────────────────────────────────────────── */}
        <section>
          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-fg">
                  {staff.first_name} {staff.last_name}
                </h3>
                <p className="mt-0.5 text-sm text-muted">{t('staff.roles.' + staff.app_role)}</p>
              </div>
              <StatusPill
                status={staff.is_active ? 'active' : 'inactive'}
                label={staff.is_active ? t('common.active') : t('common.inactive')}
              />
            </div>
          </div>
        </section>

        {/* ── Личност ──────────────────────────────────────────────────────── */}
        <section className="mt-6">
          <SectionHeader>{t('staff.form.section_person')}</SectionHeader>
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="divide-y divide-border">
              <DetailRow label={t('auth.email')}>{staff.email ?? '—'}</DetailRow>
              <DetailRow label={t('auth.phone')}>{staff.phone ?? '—'}</DetailRow>
              <DetailRow label={t('staff.form.date_of_birth')}>
                {staff.date_of_birth ? fmt.date(staff.date_of_birth) : '—'}
              </DetailRow>
              <DetailRow label={t('staff.form.national_id')}>{staff.national_id ?? '—'}</DetailRow>
              <DetailRow label={t('staff.form.address')}>{staff.address ?? '—'}</DetailRow>
              <DetailRow label={t('staff.form.emergency_contact_name')}>
                {staff.emergency_contact_name ?? '—'}
              </DetailRow>
              <DetailRow label={t('staff.form.emergency_contact_phone')}>
                {staff.emergency_contact_phone ?? '—'}
              </DetailRow>
            </div>
          </div>
        </section>

        {/* ── Вработување ──────────────────────────────────────────────────── */}
        <section className="mt-6">
          <SectionHeader>{t('staff.form.section_employment')}</SectionHeader>
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="divide-y divide-border">
              <DetailRow label={t('staff.form.role')}>
                <Chip tone="neutral">{t('staff.roles.' + staff.app_role)}</Chip>
              </DetailRow>
              <DetailRow label={t('staff.form.employment_type')}>
                {employmentLabel ?? '—'}
              </DetailRow>
              <DetailRow label={t('staff.form.hired_at')}>
                {staff.hired_at ? fmt.date(staff.hired_at) : '—'}
              </DetailRow>
              <DetailRow label={t('settings.language')}>
                {staff.locale ? staff.locale.toUpperCase() : '—'}
              </DetailRow>
            </div>
          </div>
        </section>

        {/* ── Плата ────────────────────────────────────────────────────────── */}
        <section className="mt-6">
          <SectionHeader>{t('staff.form.section_salary')}</SectionHeader>
          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            <div className="divide-y divide-border">
              <DetailRow label={t('staff.form.gross_salary')}>
                {staff.gross_salary != null ? (
                  <Money minor={staff.gross_salary} className="font-mono" />
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label={t('staff.form.net_salary')}>
                {staff.net_salary != null ? (
                  <Money minor={staff.net_salary} className="font-mono" />
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label={t('staff.form.iban')}>
                {staff.iban ? <span className="font-mono">{staff.iban}</span> : '—'}
              </DetailRow>
            </div>
          </div>
        </section>

        {/* ── Белешки ──────────────────────────────────────────────────────── */}
        {staff.notes ? (
          <section className="mt-6">
            <SectionHeader>{t('staff.form.section_notes')}</SectionHeader>
            <div className="rounded-xl border border-border bg-surface p-4">
              <p className="whitespace-pre-wrap text-sm text-fg">{staff.notes}</p>
            </div>
          </section>
        ) : null}
      </SideDrawer>

      {/* Edit form — StaffForm is its own modal (same popup as add) */}
      <StaffForm
        open={editOpen}
        onOpenChange={setEditOpen}
        staff={staff}
        onSaved={() => onOpenChange(false)}
      />
    </>
  );
}
